import get from 'lodash/get';
import type * as Types from './types';

export const Test = (item?: any): Types.IEntity.Test => ({
  id: get(item, 'id') || '',
  title: get(item, 'title') || '',
  description: get(item, 'description') || '',
  category: get(item, 'category') || '',
  collections: get(item, 'collections') ?? [],
  duration: get(item, 'duration') || get(item, 'settings.timeLimit') || 0,
  totalQuestions:
    get(item, 'total_questions') ||
    get(item, 'totalQuestions') ||
    (Array.isArray(get(item, 'sections'))
      ? get(item, 'sections').reduce((sum: number, s: any) => sum + ((s?.questions?.length ?? 0) as number), 0)
      : 0),
  passingScore: get(item, 'passing_score') || get(item, 'passingScore') || get(item, 'settings.passingScore') || 0,
  status: (['draft', 'active', 'published', 'finished', 'archived'].includes(String(get(item, 'status') || 'draft').toLowerCase())
    ? String(get(item, 'status') || 'draft').toLowerCase()
    : 'draft') as Types.IEntity.Test['status'],
  createdAt: (get(item, 'created_at') || get(item, 'createdAt')) ? new Date(get(item, 'created_at') || get(item, 'createdAt')) : new Date(),
  updatedAt: (get(item, 'updated_at') || get(item, 'updatedAt')) ? new Date(get(item, 'updated_at') || get(item, 'updatedAt')) : new Date(),
  availableUntil: get(item, 'availableUntil') ? new Date(get(item, 'availableUntil')) : undefined,
  finishedAt: get(item, 'finishedAt') ? new Date(get(item, 'finishedAt')) : undefined,
  author: {
    id: get(item, 'author.id') || '',
    name: get(item, 'author.name') || '',
    avatar: get(item, 'author.avatar') || '',
  },
  stats: {
    totalAttempts: get(item, 'stats.total_attempts') || get(item, 'stats.totalAttempts') || 0,
    activeAttempts: get(item, 'stats.active_attempts') || get(item, 'stats.activeAttempts') || 0,
    submitted: get(item, 'stats.submitted') || 0,
    // Preserve null (no denominator) — only coerce real numbers.
    assigned: typeof get(item, 'stats.assigned') === 'number' ? get(item, 'stats.assigned') : null,
    avgScore: get(item, 'stats.avg_score') || 0,
    completionRate: get(item, 'stats.completion_rate') || 0,
  },
  access: {
    mode: (['group', 'code', 'public_link', 'none', 'marketplace'].includes(get(item, 'access.mode'))
      ? get(item, 'access.mode')
      : 'none') as Types.IEntity.Test['access']['mode'],
    code: get(item, 'access.code') ?? null,
    groupName: get(item, 'access.group_name') ?? null,
  },
  difficultyLevel: get(item, 'difficultyLevel') || 'intermediate',
  subjectKey: get(item, 'subjectKey') || get(item, 'subject_key') || undefined,
  variantNumber: typeof get(item, 'variantNumber') === 'number'
    ? get(item, 'variantNumber')
    : typeof get(item, 'variant_number') === 'number'
    ? get(item, 'variant_number')
    : (get(item, 'variantNumber') || get(item, 'variant_number') ? Number(get(item, 'variantNumber') || get(item, 'variant_number')) : undefined),
  isLeadVariant: Boolean(get(item, 'isLeadVariant') ?? get(item, 'is_lead_variant')),
  tags: get(item, 'tags') || [],
  isPublic: Boolean(get(item, 'isPublic')),
  accessType: get(item, 'accessType') || 'FREE',
  priceSom: typeof get(item, 'priceSom') === 'number' ? get(item, 'priceSom') : 0,
  marketplaceStatus: get(item, 'marketplaceStatus') || 'NONE',
  marketplaceRejectionReason: get(item, 'marketplaceRejectionReason') || undefined,
  needsUpgrade: get(item, 'needs_upgrade.feature_key')
    ? {
        featureKey: get(item, 'needs_upgrade.feature_key'),
        questionCount: typeof get(item, 'needs_upgrade.question_count') === 'number' ? get(item, 'needs_upgrade.question_count') : null,
      }
    : null,
  marketplaceSubmittedAt: get(item, 'marketplaceSubmittedAt') ? new Date(get(item, 'marketplaceSubmittedAt')) : undefined,
  marketplaceApprovedAt: get(item, 'marketplaceApprovedAt') ? new Date(get(item, 'marketplaceApprovedAt')) : undefined,
});

export const TestDetails = (item?: any): Types.IEntity.TestDetails => {
  // Flatten sections into questions if API returns sections structure
  const sections = get(item, 'sections') || [];
  const questionsFromSections = sections.flatMap((s: any) =>
    (get(s, 'questions') || []).map((q: any) => Question(q))
  );
  // Fallback to root-level questions if sections not present
  const rootQuestions = get(item, 'questions') || [];
  const allQuestions = questionsFromSections.length > 0 ? questionsFromSections : rootQuestions.map((q: any) => Question(q));

  return {
    ...Test(item),
    settings: {
      ...(get(item, 'settings') || {}),
      shuffleQuestions: get(item, 'settings.shuffle_questions') || get(item, 'settings.shuffleQuestions') || false,
      shuffleAnswers:
        get(item, 'settings.shuffle_answers') ||
        get(item, 'settings.shuffleAnswers') ||
        get(item, 'settings.shuffleOptions') ||
        false,
      showResultsImmediately: get(item, 'settings.show_results_immediately') || get(item, 'settings.showResultsImmediately') || false,
      allowReview: get(item, 'settings.allow_review') || get(item, 'settings.allowReview') || false,
      proctoringEnabled: get(item, 'settings.proctoring_enabled') || get(item, 'settings.proctoring?.enabled') || false,
      requireCamera: get(item, 'settings.require_camera') || get(item, 'settings.proctoring?.requireCamera') || false,
      requireMicrophone: get(item, 'settings.require_microphone') || get(item, 'settings.proctoring?.requireMicrophone') || false,
      fullScreenMode: get(item, 'settings.full_screen_mode') || get(item, 'settings.proctoring?.fullScreenMode') || false,
      preventCopyPaste: get(item, 'settings.prevent_copy_paste') || get(item, 'settings.proctoring?.preventCopyPaste') || false,
      timeLimit: get(item, 'settings.timeLimit') || get(item, 'duration'),
      passingScore: get(item, 'settings.passingScore') || get(item, 'passingScore'),
      autoGradeOpenQuestions: Boolean(get(item, 'settings.autoGradeOpenQuestions')),
      aiGradeMinConfidence: get(item, 'settings.aiGradeMinConfidence'),
      showAiFeedbackToCandidate: Boolean(get(item, 'settings.showAiFeedbackToCandidate')),
      liveExaminer: get(item, 'settings.liveExaminer') === false ? false : undefined,
      questionReports: get(item, 'settings.questionReports') === false ? false : undefined,
    },
    questions: allQuestions,
    sections: get(item, 'sections'),
    subjectConfigs: get(item, 'subjectConfigs'),
    // Propagate backend fields that pages need (org context, free-form
    // config object, public link slug). Without these, pages like Invitations and
    // Access can't tell which org the test belongs to or what access mode is
    // currently saved.
    organizationId: get(item, 'organizationId'),
    config: get(item, 'config'),
    // IELTS-style / Digital SAT practice format (absent = standard test).
    examConfig: get(item, 'examConfig') || undefined,
    // Test content language (en/es/fr/de) — the Settings General section hydrates
    // from this; without propagating it the page loads 'en' and a Save clobbers
    // the saved language.
    language: get(item, 'language'),
    publicLinkSlug: get(item, 'publicLinkSlug'),
    // No-account public candidate link + activation window + result visibility.
    // The Access page hydrates its controls from these; without propagating them
    // here the page would always load defaults and a Save would clobber them.
    publicLink: get(item, 'publicLink'),
    availableFrom: get(item, 'availableFrom'),
    availableUntil: get(item, 'availableUntil'),
    showResultsToRespondent: get(item, 'showResultsToRespondent'),
    // Per-test feature settings persisted as top-level objects on the test.
    // The Anti-cheating settings page reads test.antiCheating to hydrate its
    // toggles; without propagating it here the page always loads defaults and
    // a subsequent Save silently overwrites the saved values with `false`.
    antiCheating: get(item, 'antiCheating'),
    gamification: get(item, 'gamification'),
    // Worksheet export defaults — the Worksheet page hydrates its controls from
    // test.worksheet; without this the page always loads defaults and a Save
    // would overwrite the saved values.
    worksheet: get(item, 'worksheet'),
  } as Types.IEntity.TestDetails;
};

export const Question = (item?: any): Types.IEntity.Question => {
  const rawType = get(item, 'type') || '';
  // Normalize type: API returns uppercase (MULTIPLE_CHOICE), preserve it or default
  const normalizedType = rawType ? rawType.toUpperCase() : 'MULTIPLE_CHOICE';

  return {
    // Spread first so every extended type-specific field (blanks, reorderItems,
    // passageText, …) survives into the editor;
    // without this the mapper silently dropped them
    // and a Save would persist the stripped question. Base fields are normalized
    // after so they always win.
    ...(item || {}),
    id: get(item, 'id') || '',
    type: normalizedType as any, // API uses uppercase enum values
    question: get(item, 'question') || get(item, 'prompt') || '',
    points: get(item, 'points') || 0,
    timeLimit: get(item, 'timeLimit') || get(item, 'time_limit'),
    options: (get(item, 'options') || []).map((opt: any) => ({
      id: get(opt, 'id') || '',
      text: get(opt, 'value') || get(opt, 'text') || '',
      value: get(opt, 'value') || get(opt, 'text') || '',
      isCorrect: get(opt, 'is_correct'),
    })),
    correctAnswer: get(item, 'correctAnswer') || get(item, 'correct_answer'),
    order: get(item, 'order') || 0,
  };
};

export const StatusCounts = (item?: any): Types.StatusCounts | undefined =>
  item
    ? {
        draft: get(item, 'draft') || 0,
        active: get(item, 'active') || 0,
        finished: get(item, 'finished') || 0,
        archived: get(item, 'archived') || 0,
      }
    : undefined;

export const Meta = (item?: any): Types.IEntity.Meta => ({
  total: get(item, 'total') || 0,
  page: get(item, 'page') || 1,
  perPage: get(item, 'per_page') || 10,
  totalPages: get(item, 'total_pages') || 0,
  statusCounts: StatusCounts(get(item, 'status_counts')),
});
